import { useEffect } from 'react';

import { isRunningInNativeApp, native } from '@lemoment/shared';

import { i18n } from '../app/core/i18n';
import { useThemeStore } from '../app/core/themeStore';

/**
 * Sync native-shell state into web app state. Subscribes to the
 * notifications the native shell pushes (theme, language, app lifecycle).
 * No-op in a plain browser.
 */
export const useNativeSync = (): void => {
  const setTheme = useThemeStore((s) => s.setTheme);

  useEffect(() => {
    if (!isRunningInNativeApp()) {
      return;
    }

    const offTheme = native.on('themeChanged', (payload) => {
      setTheme(payload.theme);
    });

    // Native → web language change. Guarded so applying it doesn't echo
    // back: i18n.on('languageChanged') notifies native on every change, so
    // skip when the language already matches to avoid a sync loop.
    const offLanguage = native.on('languageChanged', ({ language }) => {
      const next = language.split('-')[0];
      if (i18n.language?.split('-')[0] !== next) {
        void i18n.changeLanguage(next);
      }
    });

    // Placeholder for future use (e.g. refetch on foreground).
    const offAppState = native.on('appStateChanged', () => {
      // no-op
    });

    return () => {
      offTheme();
      offLanguage();
      offAppState();
    };
  }, [setTheme]);
};
